import cx from 'classnames';
import dynamic from 'next/dynamic';
import { useContext } from 'react';
import TrackWrapper from '@/common/TrackWrapper';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useConsumer } from '~/context/common/StaticContext';
import ModalContext from '~/context/home/ModalContext';
import SavedSearchesContext from '~/context/home/SavedSearchesContext';
import { useIsInExperiment, useTrackRendered } from '~/hooks/common';
import { Copy, HeroImage, HeroWrapper, SubCopy } from './Hero.styles';

const MerchSearchBar = dynamic(
  () =>
    import(
      /* webpackChunkName: "MerchSearchBar" */ '~/components/home/MerchSearchBar'
    )
);

const SearchBar = dynamic(
  () => import(/* webpackChunkName: "SearchBar" */ '@/common/SearchBar')
);

const cdnUrl = process.env.CVNA_APP_CDN_URL;

export default function Hero({ isReturningUser }) {
  const { savedSearches } = useContext(SavedSearchesContext);
  const consumer = useConsumer();
  const { isSearchBarFocused, isSearchBarModalOpen } = useContext(ModalContext);

  const isInDefaultSuggestionsExperiment = useIsInExperiment(
    'srch-home-text-search-default',
    'experiment'
  );

  const shouldFire =
    isInDefaultSuggestionsExperiment &&
    (isSearchBarFocused || isSearchBarModalOpen);
  useTrackRendered('srch-home-text-search-default', shouldFire);

  const heroImg = {
    large: `${cdnUrl}/home-assets/redesign/hero_102523.jpeg?width=1800`,
    small: `${cdnUrl}/home-assets/redesign/hero_102523.jpeg?&width=800`
  };

  const isMobile = useIsMobile();

  const viewEvent = {
    eventName: 'Viewed - Home feed section',
    customAttributes: {
      description: 'Hero'
    }
  };

  const copySectionClasses = cx(
    'flex flex-col items-center',
    isReturningUser
      ? 'mx-auto h-[182px] w-auto bg-grey-1 px-24 py-40 m:h-[235px] m:pb-[44px] m:pt-[55px]'
      : 'absolute left-1/2 top-[59px]  w-fit -translate-x-1/2  m:top-[114px]'
  );

  const searchBarClasses = cx(
    'absolute left-1/2 top-0 z-[2] mt-16 w-full max-w-[336px] -translate-x-1/2  m:mt-24 m:max-w-[680px]',
    isReturningUser ? 'top-[40%] m:top-[47%]' : 'top-[45%] m:top-[52%]'
  );

  return (
    <>
      <div className="relative">
        <TrackWrapper event={viewEvent}>
          <HeroWrapper>
            {!isReturningUser && (
              <HeroImage
                id="redesign-hero-image"
                style={{
                  backgroundImage: isMobile
                    ? `url(${heroImg.small})`
                    : `url(${heroImg.large})`
                }}
              />
            )}

            <section className={copySectionClasses}>
              <Copy
                className={isReturningUser ? 'text-blue-6' : 'text-white'}
                data-qa="hero-title"
              >
                {isReturningUser ? 'welcome back' : 'right car. right price.'}
              </Copy>

              {!isReturningUser && <SubCopy>Financing made easy.</SubCopy>}
            </section>
          </HeroWrapper>
        </TrackWrapper>

        <div className={searchBarClasses}>
          <TrackWrapper event={{ eventName: 'search_bar_viewed' }}>
            {isInDefaultSuggestionsExperiment ? (
              <MerchSearchBar />
            ) : (
              <SearchBar
                background="white"
                button="large"
                consumer={consumer}
                savedSearches={savedSearches}
              />
            )}
          </TrackWrapper>
        </div>
      </div>
    </>
  );
}
